/**
 * VendorProfileMediaDeleteDialog
 * ───────────────────────────────────────────────────────────────────────────
 * The delete confirmation for a vendor's profile images: logo, shop banner
 * and card image, for restaurants, retailer/wholesalers and healthcare
 * providers, on both the admin Edit forms and the vendor's own Profile page.
 *
 * One component instead of a window.confirm per form because:
 *   - it shows the actual images, so nobody deletes the logo thinking it was
 *     the banner;
 *   - it lists every saved image with the clicked one pre-ticked, so clearing
 *     a whole profile is one confirmation, not three;
 *   - the delete runs behind a busy state instead of a frozen page, and a
 *     partial failure (one Cloudinary destroy refused) is reported per image
 *     while the ones that did go are cleared from the form.
 *
 * The caller keeps one piece of state, the role whose Delete link was clicked:
 *
 *   const [mediaDeleteRole, setMediaDeleteRole] = useState(null);
 *   <button onClick={() => setMediaDeleteRole("cardImage")}>Delete Card Image</button>
 *   <VendorProfileMediaDeleteDialog
 *     role={mediaDeleteRole}
 *     vendorType="restaurant"
 *     vendorId={restaurant._id || restaurant.owner}
 *     vendorName={restaurant.name}
 *     media={formData}
 *     owner="the vendor"            // or "your restaurant" on the vendor's page
 *     onClose={() => setMediaDeleteRole(null)}
 *     onRemoved={(cleared) => setFormData((prev) => ({ ...prev, ...cleared }))}
 *   />
 *
 * Unsaved picks (a file chosen but not yet uploaded) never come here: the
 * forms discard those locally with no confirmation, as before.
 */
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import MediaDeleteDialog from "./MediaDeleteDialog";
import { apiRemoveVendorProfileMedia } from "../api";
import {
  listVendorProfileMedia,
  removeVendorProfileMediaRoles,
} from "../utilities/vendorProfileMedia";

const VendorProfileMediaDeleteDialog = ({
  role,
  vendorType,
  vendorId,
  vendorName,
  media,
  owner = "the vendor",
  onClose,
  onRemoved,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [retryRoles, setRetryRoles] = useState(null);

  const items = useMemo(() => listVendorProfileMedia(vendorType, media), [vendorType, media]);
  const show = !!role && items.length > 0;
  const initialKeys = retryRoles || (role ? [role] : []);

  const close = () => {
    if (busy) return;
    setError("");
    setRetryRoles(null);
    if (onClose) onClose();
  };

  const handleConfirm = async (roles) => {
    setBusy(true);
    setError("");
    try {
      const outcome = await removeVendorProfileMediaRoles(
        apiRemoveVendorProfileMedia,
        vendorType,
        vendorId,
        roles
      );
      if (outcome.removed.length && onRemoved) {
        onRemoved(outcome.cleared, outcome.removed);
      }
      if (outcome.removed.length) {
        toast.success(
          outcome.removed.length === 1
            ? `Image removed from ${owner} and Cloudinary.`
            : `${outcome.removed.length} images removed from ${owner} and Cloudinary.`
        );
      }
      if (outcome.failed.length) {
        const labels = new Map(items.map((it) => [it.key, it.label]));
        setError(
          outcome.failed
            .map((f) => `${labels.get(f.role) || f.role}: ${f.message}`)
            .join(" ")
        );
        // Keep the dialog open on what is still there, with the failures
        // ticked so one more click retries exactly those.
        setRetryRoles(outcome.failed.map((f) => f.role));
        return;
      }
      setRetryRoles(null);
      if (onClose) onClose();
    } finally {
      setBusy(false);
    }
  };

  const title = vendorName ? `Delete images from ${vendorName}?` : "Delete images?";

  return (
    <MediaDeleteDialog
      show={show}
      title={items.length > 1 ? title : `Delete ${items[0] ? items[0].label.toLowerCase() : "image"}?`}
      intro={
        items.length > 1
          ? `Tick the images to remove. They are deleted from ${owner} and from Cloudinary.`
          : `This permanently removes the ${items[0] ? items[0].label.toLowerCase() : "image"} from ${owner} and from Cloudinary.`
      }
      items={items}
      initialKeys={initialKeys}
      noun="image"
      busy={busy}
      error={error}
      onConfirm={handleConfirm}
      onCancel={close}
    />
  );
};

export default VendorProfileMediaDeleteDialog;
